<template lang="html">
  <div class="projects-map">
    <GmapMap
      id="projects-map"
      :center="mapCenter"
      :zoom="mapZoom"
      map-type-id="roadmap"
      class="projects-map"
      :options="mapSettings"
      ref="map"
    >
      <gmap-info-window
        v-if="selectedProject"
        :options="infoOptions"
        :position="infoWindowPos"
        :opened="infoWinOpen"
        @closeclick="infoBoxClosed()"
      >
        <div
          class=" project-info flex"
          @click="$emit('project-selected', selectedProject)"
        >
          <div class="project-name">
            <p class="title is-size-6">{{ selectedProject.name }}</p>
            <p class="subtitle is-size-7 has-text-grey-medium">
              {{ selectedProject.address.street }}
            </p>
          </div>
          <div>
            <span slot="trigger" class="icon">
              <i class="las la-2x la-angle-right" aria-hidden="true"></i>
            </span>
          </div>
        </div>
      </gmap-info-window>

      <GmapMarker
        v-for="(project, index) in availableLocations"
        :key="index"
        :position="positionForLocation(project.location)"
        :clickable="true"
        :draggable="false"
        :icon="{
          path: markerOptions.path,
          fillColor: '#ff0000',
          fillOpacity: 1,
          anchor: { x: 24, y: 72 },
          strokeWeight: 1.5,
          strokeOpacity: 0.7,
          strokeColor: '#fff',
          scale: 0.5,
        }"
        @click="pinSelected(project)"
      />
    </GmapMap>
  </div>
</template>

<script>
import Vue from 'vue';
import maps from '@/maps';

export default {
  name: 'projects-map',
  // share common functionality with component mixins
  mixins: [],
  props: {
    projects: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  // variables
  data() {
    return {
      selectedProject: null,
      mapCenter: { lat: 39.77, lng: -86.17 },
      mapZoom: 10,
      infoWindowPos: { lat: 0, lng: 0 },
      infoWinOpen: false,
      infoOptions: {
        pixelOffset: {
          width: 0,
          height: -35,
        },
      },
      markerOptions: maps.marker,
      mapSettings: maps.settings,
    };
  },
  computed: {
    availableLocations() {
      let filtered =
        (this.projects &&
          this.projects.filter(project => {
            const include =
              project.location &&
              project.location.latitude &&
              project.location.longitude;
            return include;
          })) ||
        null;
      return filtered;
    },
  },
  components: {},
  // methods
  watch: {
    $route() {
      Vue.$gmapDefaultResizeBus.$emit('resize');
    },
    availableLocations() {
      this.zoomToFitProjects();
    },
  },
  methods: {
    zoomToFitProjects() {
      if (this.availableLocations && this.availableLocations.length > 0) {
        // surround with try because google.maps may not exist yet.
        try {
          let mapElement = document.querySelector('#projects-map');
          if (!mapElement) return;

          let markers = this.availableLocations.map(project => ({
            lat: project.location.latitude,
            lng: project.location.longitude,
          }));
          const bounds = new window.google.maps.LatLngBounds();
          if (markers.length > 2) {
            for (let m of markers) {
              bounds.extend(m);
            }
          }
          let mapRect = mapElement.getBoundingClientRect();
          let newZoom = this.getBoundsZoomLevel(bounds, mapRect);
          if (newZoom) {
            this.mapZoom = newZoom;
            this.mapCenter = bounds.getCenter();
          } else {
            this.mapCenter = markers.length > 0 && markers[0];
          }
        } catch (err) {
          window.console.error(err);
        }
        Vue.$gmapDefaultResizeBus.$emit('resize');
      }
    },
    getBoundsZoomLevel(bounds, mapDim) {
      // https://stackoverflow.com/a/13274361
      var WORLD_DIM = { height: 256, width: 256 };
      var ZOOM_MAX = 21;

      function latRad(lat) {
        var sin = Math.sin((lat * Math.PI) / 180);
        var radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
      }

      function zoom(mapPx, worldPx, fraction) {
        return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
      }

      var ne = bounds.getNorthEast();
      var sw = bounds.getSouthWest();

      var latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

      var lngDiff = ne.lng() - sw.lng();
      var lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

      var latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
      var lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

      return Math.min(latZoom, lngZoom, ZOOM_MAX);
    },
    positionForLocation(location) {
      return {
        lat: location.latitude,
        lng: location.longitude,
      };
    },
    pinSelected(project) {
      console.log('pin selected - ', project.name);

      if (this.selectedProject && project.id === this.selectedProject.id) {
        this.infoWinOpen = false;
        this.selectedProject = null;
        return;
      }

      let pos = this.positionForLocation(project.location);

      this.selectedProject = project;
      this.infoWindowPos = pos;
      this.infoWinOpen = true;
    },

    infoBoxClosed() {
      this.infoWinOpen = false;
      this.selectedProject = null;
    },
  },
  mounted() {
    Vue.$gmapDefaultResizeBus.$emit('resize');
    this.zoomToFitProjects();
  },
};
</script>

<style lang="stylus">

$radius = 5px
.projects-map {
    width: 100%;
    height: 100%;
}

button.gm-ui-hover-effect {
  // Hide close button on google maps info window
  display: none !important;
}

.infobox {
  background: none repeat scroll 0 0 #fff
  color: #484848
  line-height: 20px
  position: absolute
  border-radius: $radius
  box-shadow: 0 0px 20px rgba(0,0,0,0.2)
  cursor: pointer

  &:before {
    border-top: 15px solid #fff
    border-right: 15px solid transparent
    border-left: 15px solid transparent
    bottom: -15px
    content: ''
    display: block
    left: 50px
    margin-left: -15px
    position: absolute
  }
  .close {
    background: none repeat scroll 0 0
    cursor: pointer
    float: right
    font-family: 'Rubik'
    font-size: 17px
    height: 25px
    line-height: 22px
    position: relative
    right: 0
    text-align: center
    top: 0
    width: 25px
  }

  .infobox-content {
    font-family: 'Rubik'
    font-size: 1.5em
    height: 100%
    border-radius: $radius
    overflow: hidden

    .project-info {
      display: flex
      align-items: center
      justify-content: center
      height: 100%

      .project-name {
        flex: 1
        padding: 10px 5px 10px 15px

        .title {
          font-size: 16px
          margin-bottom:20px
        }
        .subtitle {
          font-size: 12px
        }
      }

      .box-info {
        height: 100%
        background: #ff0000
        color: white
        display: flex
        width: 85px
        align-items: center
        justify-content: center

        div:first-of-type {
          flex: 1
          display: flex
          flex-direction: column
          align-items: center
          justify-content: center
        }
        div:last-of-type {
          margin-left: -0.7em
          margin-right: 0.1em
        }
      }
    }
  }
}
</style>
